import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '@clerk/clerk-react';
import dayjs from 'dayjs';
import { MAIL_TRIAGE_DESTINATION_OPTIONS } from 'mail-destination-config';
import { 
  WandSparkles, 
  Maximize2, 
  Mails, 
  Archive,
  Reply,
  List,
  DollarSign,
  Flag,
  InboxIcon,
  Settings,
  RefreshCcw,
  CheckCircle,
  TicketPlusIcon,
  Flame,
  ChevronDown,
  Filter,
  Rows3,
  Undo,
  X
} from 'lucide-react';

import {
  updateUserPreferenceService,
  updateUserScheduleService
} from '../../utils/userService';
import { formatEmailDate, formatEmailSenderName } from '../../utils/formatter';
import { resolveActionDestinationName } from '../../utils/actionDestination';
import { canDisplayMailAction, canWriteMail, getMailboxSyncPresentation, resolveMailProvider } from '../../utils/mailProvider';
import EmailCard from './EmailCard';
import EmailActionRibbon from './EmailActionRibbon';
import EmailActionItems from './EmailActionItems';
import ActionDetailsModal from './ActionDetailsModal';
import EmailPrefsModal from './EmailPrefsModal';
import EmailCustomFolders from './EmailCustomFolders';
import CustomFolderModal from './CustomFolderModal';
import { setIntegrationDestinations } from '../../reducers/digestReducer';
import EmailProcessingInstructions from './EmailProcessingInstructions';
import EmailProcessingSchedule from './EmailProcessingSchedule';
import EmailProcessingOverlay from './EmailProcessingOverlay';
import {
  presentMailFailure,
  presentMailFailures,
} from '../../utils/mailFailurePresentation';
import { clearScheduledMailFailures } from '../../reducers/emailJobReducer';

export const getCanonicalDestinationOptions = () => MAIL_TRIAGE_DESTINATION_OPTIONS;

const FilteredEmailView = ({ 
  showEmailTriageOnboardingModal,
  totalDigestItems,
  remainingDigestItems,
  handleUndoAutomatedAction,
  acknowledgeAction,
  handleUndoAcknowledge,
  isLoadingDigest,
  generateEmailDigest,
  getEmailContent,
  isCardVisible,
  setInputValues,
  inputValues,
  setIsCardVisible,
  isProjectModalOpen,
  setIsProjectModalOpen,
  isProcessingEmailActions,
  isProcessingEvents,
  currentEmailProcessingIndex,
  getAllEmails,
  itemsToRender,
  handleArchiveEmail,
  handleMoveEmail,
  handleCreateTask,
  handleReplyToEmail,
  handleAddEmailToProject,
  handleViewTask,
  getActiveTask,
  getActiveProject,
  handleViewProject,
  readOnlyMode,
  setReadOnlyMode,
  showScheduleUpdateNotification,
  activeMailbox,
  syncError,
  onRetrySync,
  mailFailurePresentation,
  mailFailureIdentity,
  onRecoverMailFailure,
  onRecoverAsyncMailFailure,
  isRecoveringMailFailure = false,
}) => {
  const dispatch = useDispatch();
  const { getToken } = useAuth();
  const currentDigest = useSelector(state => state.digestList.currentDigest);
  // Custom Folders & Smart Email Routing: FilteredEmailView owns the single
  // shared CustomFolderModal (D-08) and pins it to the mailbox it was opened
  // for, so a mailbox switch mid-dialog closes it rather than silently
  // reusing a stale integration id.
  const [isCustomFolderModalOpen, setIsCustomFolderModalOpen] = useState(false);
  const customFolderModalMailboxIdRef = useRef(null);
  const manualJobFailures = useSelector(state => state.emailJobs?.failures ?? []);
  const manualFailureMailboxId = useSelector(
    state => state.emailJobs?.manualFailureMailboxId ?? null,
  );
  const scheduledJobFailures = useSelector(state => state.emailJobs?.scheduledFailures ?? []);
  const [triageFailureDismissed, setTriageFailureDismissed] = useState(false);
  const [syncFailureDismissed, setSyncFailureDismissed] = useState(false);
  const [isAsyncRecoveryPending, setIsAsyncRecoveryPending] = useState(false);
  const [asyncRecoveryVersion, setAsyncRecoveryVersion] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [isQueuedView, setIsQueuedView] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isEmailPrefsModalOpen, setIsEmailPrefsModalOpen] = useState(false);
  const [emailPrefsCurrentView, setEmailPrefsCurrentView] = useState("main");
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isListView, setIsListView] = useState(true);
  const retrySyncRef = useRef(null);
  const lastFocusedSyncFailure = useRef(null);
  const asyncRecoveryRef = useRef(null);
  const asyncRecoveryStarted = useRef(false);
  const lastFocusedAsyncFailure = useRef(null);
  const mailbox = activeMailbox || currentDigest?.mailbox;
  const mailboxScheduledFailures = typeof mailbox?.id === 'string'
    ? scheduledJobFailures
        .filter(({ mailboxId }) => mailboxId === mailbox.id)
        .map(({ error }) => error)
    : [];
  const mailboxManualFailures = mailbox?.id === manualFailureMailboxId
    ? manualJobFailures
    : [];
  const jobFailures = [...mailboxManualFailures, ...mailboxScheduledFailures];
  const jobFailureCount = jobFailures.length;
  const resolvedProvider = resolveMailProvider({
    mailbox,
    email: currentDigest?.content?.find((email) => email?.provider),
  });
  const mailboxContextUnavailable = Boolean(currentDigest) && (
    !resolvedProvider || resolvedProvider === 'unknown'
  );
  const trustedProvider = ['google', 'microsoft'].includes(resolvedProvider)
    ? resolvedProvider
    : null;
  const focusedMailFailure = mailFailurePresentation
    ?? (mailboxContextUnavailable
      ? presentMailFailure(null)
      : syncError
        ? presentMailFailure(
            syncError,
            trustedProvider ? { expectedProvider: trustedProvider } : {},
          )
        : null);
  const focusedMailFailureIdentity = mailFailureIdentity
    ?? focusedMailFailure?.identity
    ?? null;
  const focusedMailFailureBusy = isRecoveringMailFailure
    || (!mailFailurePresentation && isLoadingDigest);
  const handleFocusedMailFailureRecovery = mailFailurePresentation
    ? onRecoverMailFailure
    : (onRetrySync || generateEmailDigest);
  const asyncMailFailurePresentation = jobFailureCount > 0
    ? presentMailFailures(
        jobFailures,
        trustedProvider ? { expectedProvider: trustedProvider } : {},
      )
    : null;
  const asyncFailureIdentity = asyncMailFailurePresentation
    ? `${mailbox?.id ?? 'no-mailbox'}:${asyncMailFailurePresentation.identity}:${jobFailureCount}:${asyncRecoveryVersion}`
    : null;
  const asyncRecoveryBusy = isRecoveringMailFailure || isAsyncRecoveryPending;
  const syncPresentation = getMailboxSyncPresentation(mailbox);
  const getMailWriteContext = (email) => ({ mailbox, email });
  const canWriteEmail = (email, operation) => canWriteMail(getMailWriteContext(email), operation);
  const visibleEmailActions = (email) => email?.actions?.filter(
    (action) => canDisplayMailAction(getMailWriteContext(email), action)
  );

  useEffect(() => {
    if (!focusedMailFailureIdentity) {
      lastFocusedSyncFailure.current = null;
      setSyncFailureDismissed(false);
      return;
    }

    if (lastFocusedSyncFailure.current !== focusedMailFailureIdentity) {
      lastFocusedSyncFailure.current = focusedMailFailureIdentity;
      setSyncFailureDismissed(false);
      retrySyncRef.current?.focus();
    }
  }, [focusedMailFailureIdentity]);

  // A new run clears the recorded failures, so drop the dismissal with them —
  // otherwise dismissing once would suppress every later failure too.
  useEffect(() => {
    if (jobFailureCount === 0) {
      setTriageFailureDismissed(false);
      lastFocusedAsyncFailure.current = null;
    }
  }, [jobFailureCount]);

  useEffect(() => {
    if (
      !triageFailureDismissed
      && asyncFailureIdentity
      && lastFocusedAsyncFailure.current !== asyncFailureIdentity
    ) {
      lastFocusedAsyncFailure.current = asyncFailureIdentity;
      asyncRecoveryRef.current?.focus();
    }
  }, [asyncFailureIdentity, triageFailureDismissed]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    handleResize() // Initialize on mount
    
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const getIsMobileOnly = () => {
    return windowWidth < 768
  }
  
  useEffect(() => {
    if (searchTerm && searchTerm.length > 2) {
      setActiveFilters([]);
      setIsQueuedView(false);
    }
  }, [searchTerm]);

  const handleClickOutsideFilterDropdown = useCallback((event) => {
    if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
      setIsFilterDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideFilterDropdown);
      document.addEventListener('touchstart', handleClickOutsideFilterDropdown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
      document.removeEventListener('touchstart', handleClickOutsideFilterDropdown);
    };
  }, [isFilterDropdownOpen, handleClickOutsideFilterDropdown]);

  const isTodaysDigest = currentDigest?.title === dayjs().format("MMMM D, YYYY");

  const getMoveLabelName = (action) => resolveActionDestinationName(action, currentDigest);

  const matchesMoveLabel = (email, matcher) => (
    email.actions?.some(action => (
      action.type === 'move-to-label' && matcher(getMoveLabelName(action))
    ))
  );

  const filterTypes = {
    'replied': {
      name: 'Replied',
      label: <Reply size={22} />,
      filter: email => email.actions?.some(action => action.type === 'reply'),
      count: currentDigest?.content?.filter(email => 
        email.actions?.some(action => action.type === 'reply')
      )?.length
    },
    'tasks': {
      name: 'Tasks',
      label: <List size={22} />,
      filter: email => email.actions?.some(action => action.type === 'create-a-task'),
      count: currentDigest?.content?.filter(email => 
        email.actions?.some(action => action.type === 'create-a-task')
      )?.length
    },
    'maintenance-tickets': {
      name: 'Tickets',
      label: <TicketPlusIcon size={22} />,
      filter: email => email.actions?.some(action => action.type === 'create-a-maintenance-ticket'),
      count: currentDigest?.content?.filter(email => 
        email.actions?.some(action => action.type === 'create-a-maintenance-ticket')
      )?.length
    },
    'urgent': {
      name: 'Urgent',
      label: <Flame size={22} />,
      filter: email => matchesMoveLabel(
        email,
        (destination) => destination === 'urgent' || destination === 'Urgent'
      ),
      count: currentDigest?.content?.filter(email => 
        matchesMoveLabel(
          email,
          (destination) => destination === 'urgent' || destination === 'Urgent'
        )
      )?.length
    },
    'financial': {
      name: 'Financial',
      label: <DollarSign size={22} />,
      filter: email => matchesMoveLabel(
        email,
        (destination) => destination === 'financial' || destination === 'Financial'
      ),
      count: currentDigest?.content?.filter(email => 
        matchesMoveLabel(
          email,
          (destination) => destination === 'financial' || destination === 'Financial'
        )
      )?.length
    },
    'for-review': {
      name: 'For Review',
      label: <Flag size={22} />,
      filter: email => matchesMoveLabel(
        email,
        (destination) => destination === 'for_review' || destination === 'For review'
      ),
      count: currentDigest?.content?.filter(email => 
        matchesMoveLabel(
          email,
          (destination) => destination === 'for_review' || destination === 'For review'
        )
      )?.length
    },
    'archived': {
      name: 'Archived',
      label: <Archive size={22} />,
      filter: email => email.actions?.some(action => action.type === 'archive'),
      count: currentDigest?.content?.filter(email => 
        email.actions?.some(action => action.type === 'archive')
      )?.length
    },
    'unreviewed': {
      name: 'Unreviewed',
      label: <InboxIcon size={22} />,
      filter: email => !email.actions || email.actions?.length === 0,
      count: remainingDigestItems
    }
  };

  const needsReviewCount = totalDigestItems - currentDigest?.content?.filter(email => email.archived === true)?.length - currentDigest?.content?.filter(email => !email.actions)?.length || 0;

  const dropdownFilterTypes = [
    'urgent',
    'for-review',
    'replied',
    'tasks',
    'maintenance-tickets',
    'financial',
    'archived',
  ];

  const collapseCardVisibility = () => {
    setIsCardVisible(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(key => {
        newState[key] = false;
      });
      return newState;
    });
  };

  const handleToggleFilter = (type) => {
    setSearchTerm('');
    setIsQueuedView(false);
    setActiveFilters(prev => {
      const isSelected = prev.includes(type);
      return isSelected ? prev.filter(f => f !== type) : [...prev, type];
    });
    collapseCardVisibility();
  };

  const handleSelectAllFilters = () => {
    setSearchTerm('');
    setIsQueuedView(false);
    setActiveFilters([...dropdownFilterTypes]);
    collapseCardVisibility();
  };

  const handleQueuedViewToggle = () => {
    setSearchTerm('');
    const nextQueuedView = !isQueuedView;
    setIsQueuedView(nextQueuedView);
    if (nextQueuedView) {
      setActiveFilters([]);
    }
    collapseCardVisibility();
  };

  const handleSummaryTileClick = (filterKeys) => {
    setSearchTerm('');
    setIsQueuedView(false);
    setActiveFilters(filterKeys);
    collapseCardVisibility();
  };

  const getGroupCounts = (types) => {
    const emails = currentDigest?.content?.filter(email =>
      types.some(type => filterTypes[type].filter(email))
    ) || [];
    return {
      awaitingReview: emails.filter(email => !email.archived).length,
      total: emails.length,
    };
  };

  const getAIActionsTaken = () => {
    return currentDigest?.content?.filter(
      (email) =>
        email.actions?.filter((action) => action?.actor === "assistant").length
    )?.length;
  };

  const actionSummaryTiles = [
    {
      label: 'Urgent',
      filterKeys: ['urgent'],
      ...getGroupCounts(['urgent']),
      tileStyles: 'bg-orange-50 border-orange-100 text-orange-800 hover:bg-orange-100 hover:shadow-md hover:-translate-y-0.5',
      totalLabel: 'total urgent',
    },
    {
      label: 'Action needed',
      filterKeys: ['tasks', 'maintenance-tickets'],
      ...getGroupCounts(['tasks', 'maintenance-tickets']),
      tileStyles: 'bg-emerald-50 border-emerald-100 text-emerald-800 hover:bg-emerald-100 hover:shadow-md hover:-translate-y-0.5',
      totalLabel: 'total actions',
    },
    {
      label: 'Requires review',
      filterKeys: ['for-review', 'financial'],
      ...getGroupCounts(['for-review', 'financial']),
      tileStyles: 'bg-indigo-50 border-indigo-100 text-indigo-800 hover:bg-indigo-100 hover:shadow-md hover:-translate-y-0.5',
      totalLabel: 'total to review',
    },
    {
      label: 'Awaiting reply',
      filterKeys: ['replied'],
      ...getGroupCounts(['replied']),
      tileStyles: 'bg-cyan-50 border-cyan-100 text-cyan-800 hover:bg-cyan-100 hover:shadow-md hover:-translate-y-0.5',
      totalLabel: 'total replies',
    }
  ];

  const triagedCount = getAIActionsTaken() || 0;
  const hasActiveFilters = activeFilters.length > 0;

  const getActiveFilterLabel = () => {
    if (!hasActiveFilters) return 'Filter emails';
    if (activeFilters.length === 1) {
      return filterTypes[activeFilters[0]]?.name || activeFilters[0];
    }
    return `${activeFilters.length} filters`;
  };

  const threadMatchesActiveFilters = (email) =>
    activeFilters.some(type => filterTypes[type].filter(email));

  const getFilteredThreads = () => {
    if (searchTerm) {
      return itemsToRender.filter(thread => {
        return thread[0]?.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        thread[0]?.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        thread[0]?.snippet?.toLowerCase().includes(searchTerm.toLowerCase())
      });
    }
    if (isQueuedView) {
      return itemsToRender.filter(thread => filterTypes.unreviewed.filter(thread[0]));
    }
    if (hasActiveFilters) {
      return itemsToRender.filter(thread => threadMatchesActiveFilters(thread[0]));
    }
    if (!searchTerm) {
      return itemsToRender.filter(thread =>
        filterTypes.urgent.filter(thread[0]) && !thread[0]?.archived && thread[0]?.actions
      );
    }
    return itemsToRender;
  };

  const handleNavigateToInstructions = () => {
    setEmailPrefsCurrentView("instructions")
  }

  const handleNavigateToSchedule = () => {
    setEmailPrefsCurrentView("schedule")
  }

  const handleNavigateToFolders = () => {
    setEmailPrefsCurrentView("folders")
  }

  const handleBackToMain = () => {
    setEmailPrefsCurrentView("main")
  }

  const handleClose = () => {
    setIsEmailPrefsModalOpen(false)
    setEmailPrefsCurrentView("main")
  }

  // Custom Folders & Smart Email Routing (D-08): Add Folder never moves the
  // current email -- EmailCard's sentinel handling already guarantees that;
  // this only opens the shared dialog, pinned to the mailbox open right now.
  const handleOpenCustomFolderModal = () => {
    customFolderModalMailboxIdRef.current = mailbox?.id ?? null;
    setIsCustomFolderModalOpen(true);
  };

  const handleCloseCustomFolderModal = () => {
    setIsCustomFolderModalOpen(false);
  };

  // Refreshes only the matching integration's destination state; never
  // calls sync inbox or replaces digest content.
  const handleCustomFolderSaved = ({ mailIntegrationId, destinations }) => {
    dispatch(setIntegrationDestinations({ mailIntegrationId, destinations }));
  };

  // A mailbox switch while the dialog is open discards it rather than
  // reusing a now-stale pinned integration id.
  useEffect(() => {
    if (
      isCustomFolderModalOpen
      && customFolderModalMailboxIdRef.current !== null
      && customFolderModalMailboxIdRef.current !== (mailbox?.id ?? null)
    ) {
      setIsCustomFolderModalOpen(false);
    }
  }, [isCustomFolderModalOpen, mailbox?.id]);

  const handleSaveInstructions = async (preferences) => {
    await updateUserPreferenceService(preferences);
  }

  const handleSaveSchedule = async (scheduleData) => {
    await updateUserScheduleService(scheduleData);
    showScheduleUpdateNotification();
  }

  const handleActionClick = (action, email) => {
    if (!canDisplayMailAction(getMailWriteContext(email), action)) {
      return;
    }
    setSelectedAction(action);
    setSelectedEmail(email);
    setIsActionModalOpen(true);
  }

  const renderHighlightedSubject = (subject) => {
    if (!searchTerm) {
      return subject;
    }

    return subject?.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, index) => (
      <span key={index} className={part.toLowerCase() === searchTerm.toLowerCase() ? 'bg-yellow-200' : ''}>
        {part}
      </span>
    ));
  };

  const renderThreadActionButtons = (email) => (
    <div className="flex shrink-0 justify-end items-center">
      {!isQueuedView && (
        <div className="flex flex-row justify-start items-center h-fit gap-2">
          {/* A manual "Mark Review Completed" writes no action row, so the
              ActionDetailsModal revert (which filters assistant actions) can
              never reach it -- without this branch a mis-click is
              unrecoverable. Do not gate this on provider: the undo goes
              through the shared mail-write predicate in Digest.js, and the
              digest-side un-archive must work for every mailbox. */}
          {email?.archived === true ? (
            <button
              title="Unarchive Email"
              data-amp-track-section="Digest Email List"
              data-amp-track-action="Undo_Archive_Click"
              data-amp-track-description="Unarchive Email"
              onClick={() => handleUndoAcknowledge?.(email)}
              className="p-1 text-xs bg-transparent border-solid border border-stone-300 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors flex items-center gap-1"
            >
              <Undo size={isListView ? 18 : 22} />
            </button>
          ) : (
            <button
              title="Mark Review Completed"
              data-amp-track-section="Digest Email List"
              data-amp-track-action="Mark_Review_Completed_Click"
              data-amp-track-description="Mark Review as Completed"
              onClick={() => acknowledgeAction(email?.id)}
              className="p-1 text-xs bg-transparent border-solid border border-stone-300 text-stone-500 hover:text-stone-700 hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1"
            >
              <CheckCircle size={isListView ? 18 : 22} />
            </button>
          )}
        </div>
      )}
      {canWriteEmail(email, 'archive_email') && <button
        onClick={() => handleArchiveEmail(email)}
        title="Archive Email"
        data-amp-track-section="Digest Email List"
        data-amp-track-action="Archive_Click"
        data-amp-track-description="Archive Email"
        disabled={email?.archived === true && email?.actions?.some(action => action.type === 'archive')}
        className="p-1 ml-2 text-xs bg-transparent border-solid border border-stone-300 text-stone-500 hover:text-stone-700 hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1"
      >
        <Archive size={isListView ? 18 : 22} />
      </button>}
      <button
        title="View Email"
        onClick={() => getEmailContent(email?.id)}
        data-amp-track-section="Digest Email List"
        data-amp-track-action="Expand_Email_Click"
        data-amp-track-description="Expand Email"
        className="p-1 ml-2 text-xs bg-transparent border-solid border border-stone-300 text-stone-500 hover:text-stone-700 hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1"
      >
        <Maximize2 size={isListView ? 18 : 22} />
      </button>
    </div>
  );

  const handleCloseActionModal = () => {
    setIsActionModalOpen(false);
    setSelectedAction(null);
    setSelectedEmail(null);
  }

  const handleAcknowledgeAction = async (action) => {
    if (selectedEmail) {
      await acknowledgeAction(selectedEmail.id);
      handleCloseActionModal();
    }
  }

  const handleUndoAutomatedActionWithUpdate = async (emailId, actionType) => {
    // Call the original revert function
    await handleUndoAutomatedAction(emailId, actionType);
    
    // Update selectedEmail with fresh data from Redux store if this is the currently selected email
    if (selectedEmail && selectedEmail.id === emailId) {
      const updatedEmail = currentDigest?.content?.find(email => email.id === emailId);
      if (updatedEmail) {
        setSelectedEmail(updatedEmail);
        setSelectedAction(null);
      }
    }
  }

  const handleAsyncMailFailureRecovery = async () => {
    if (
      !asyncMailFailurePresentation
      || !asyncMailFailurePresentation.recoveryIntent
      || asyncRecoveryStarted.current
      || typeof onRecoverAsyncMailFailure !== 'function'
    ) return;

    asyncRecoveryStarted.current = true;
    setIsAsyncRecoveryPending(true);
    try {
      const recovered = await onRecoverAsyncMailFailure(
        asyncMailFailurePresentation,
        mailbox?.id,
      );
      if (recovered === false) {
        setAsyncRecoveryVersion((version) => version + 1);
      } else if (recovered === true && mailboxScheduledFailures.length > 0) {
        dispatch(clearScheduledMailFailures(mailbox?.id));
      }
    } finally {
      asyncRecoveryStarted.current = false;
      setIsAsyncRecoveryPending(false);
    }
  };

  return (
    <div className="relative min-h-[200px]">
      <div className="flex flex-row justify-between mb-3">
        <div className={`flex flex-row items-center ${(isProcessingEmailActions || getAIActionsTaken() > 0) ? '' : 'opacity-50'}`}>
          <WandSparkles size={28} className="ml-3" />
          <p className="my-2 ml-2 text-2xl font-bold">
            {needsReviewCount} Email{needsReviewCount === 1 ? '' : 's'} Require{needsReviewCount === 1 ? 's' : ''} Attention
          </p>
        </div>
        <div className="flex flex-row items-center">
          <button
            onClick={onRetrySync || generateEmailDigest}
            data-amp-track-section="Digest Email List"
            data-amp-track-action="Sync_Inbox_Click"
            data-amp-track-description={syncPresentation.trackingDescription}
            aria-busy={isLoadingDigest || isRecoveringMailFailure}
            disabled={isLoadingDigest || isRecoveringMailFailure || !isTodaysDigest || isProcessingEmailActions}
            className={`flex flex-row items-center justify-start text-md font-semibold gap-2 mx-2 px-2 py-1 border-solid border rounded-3xl border-slate-300 text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white
              ${
                (!isTodaysDigest || isLoadingDigest || isRecoveringMailFailure || isProcessingEmailActions)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            `}
          >
            <div className="flex flex-row items-center gap-2">
              {isLoadingDigest ? (
                <RefreshCcw size={16} className="animate-spin" />
              ) : (
                <img
                  src={syncPresentation.iconSrc}
                  alt={syncPresentation.iconAlt}
                  className="w-5 h-5"
                />
              )}
              <span>{isLoadingDigest ? 'Syncing your inbox…' : 'Sync Inbox'}</span>
            </div>
          </button>
          
          <button
            data-amp-track-section="Digest Email List"
            data-amp-track-action="Email_Preferences_Click"
            data-amp-track-description="Open Email Preferences"
            onClick={() => setIsEmailPrefsModalOpen(true)}
            className="flex flex-row items-center justify-start text-md font-semibold gap-2 ml-0 px-3 py-2 border-solid border rounded-3xl border-slate-300 text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white"
          >
            <List size={18} />
            <Settings size={18} />
          </button>
        </div>
      </div>

      <div className="relative min-h-[200px]">
        {focusedMailFailure && !syncFailureDismissed && (
          <div
            role="alert"
            className="mb-4 w-full max-w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700"
          >
            <p className="min-w-0 break-words whitespace-normal leading-relaxed">
              <span className="font-semibold">{focusedMailFailure.title}</span>{' '}
              {focusedMailFailure.detail}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                ref={retrySyncRef}
                type="button"
                onClick={handleFocusedMailFailureRecovery}
                disabled={focusedMailFailureBusy}
                aria-busy={focusedMailFailureBusy}
                className="min-h-11 max-w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 whitespace-normal break-words hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {focusedMailFailureBusy
                  ? focusedMailFailure.busyLabel
                  : focusedMailFailure.actionLabel}
              </button>
              <button
                type="button"
                onClick={() => setSyncFailureDismissed(true)}
                aria-label="Dismiss triage failure notice"
                className="min-h-11 min-w-11 shrink-0 rounded p-2 text-slate-600 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        {isProcessingEmailActions && (
          <EmailProcessingOverlay
            currentEmailProcessingIndex={currentEmailProcessingIndex}
            totalEmails={getAllEmails?.()?.length || remainingDigestItems}
            isProcessingEvents={isProcessingEvents}
            readOnlyMode={readOnlyMode}
          />
        )}

        {/* The overlay unmounts the moment the last job settles, so a failure
            has to be reported here or it is never seen at all. */}
        {!isProcessingEmailActions && jobFailureCount > 0 && !triageFailureDismissed && (
          <div
            role="alert"
            className="mb-3 w-full max-w-full overflow-hidden rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
          >
            <p className="font-medium tabular-nums">
              {jobFailureCount === 1
                ? 'One batch of emails couldn’t be triaged.'
                : `${jobFailureCount} batches of emails couldn’t be triaged.`}
            </p>
            <p className="mt-1 min-w-0 break-words whitespace-normal text-red-700">
              <span className="font-semibold">{asyncMailFailurePresentation.title}</span>{' '}
              {asyncMailFailurePresentation.detail}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {asyncMailFailurePresentation.recoveryIntent && (
                <button
                  ref={asyncRecoveryRef}
                  type="button"
                  onClick={handleAsyncMailFailureRecovery}
                  disabled={asyncRecoveryBusy}
                  aria-busy={asyncRecoveryBusy}
                  className="min-h-11 max-w-full rounded-md border border-red-300 px-3 py-2 whitespace-normal break-words text-red-800 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {asyncRecoveryBusy
                    ? asyncMailFailurePresentation.busyLabel
                    : asyncMailFailurePresentation.actionLabel}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setTriageFailureDismissed(true);
                  if (mailboxScheduledFailures.length > 0) {
                    dispatch(clearScheduledMailFailures(mailbox?.id));
                  }
                }}
                aria-label="Dismiss triage failure notice"
                className="min-h-11 min-w-11 shrink-0 rounded p-2 text-red-700 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        <div className={isProcessingEmailActions ? 'pointer-events-none' : ''}>
          {/* Search for emails */}
          <div className="flex flex-row items-center justify-start mb-2">
            <input 
              onChange={(e) => setSearchTerm(e.target.value)}
              data-amp-track-section="Digest Email List"
              data-amp-track-action="Email_Search_Change"
              data-amp-track-description="Search Emails"
              value={searchTerm}
              type="text" 
              placeholder="Search emails" 
              className="w-full p-2 border border-slate-200 rounded-md mb-4" 
            />
          </div>

          {!isProcessingEmailActions && searchTerm === '' && (
            <div className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {actionSummaryTiles.map((tile) => (
                  <button
                    key={`summary-tile-${tile.label}`}
                    onClick={() => handleSummaryTileClick(tile.filterKeys)}
                    data-amp-track-section="Digest Email List"
                    data-amp-track-action="Summary_Tile_Click"
                    data-amp-track-description={tile.label}
                    className={`flex flex-col items-center justify-center text-center rounded-xl ml-0 border px-3 py-4 min-h-[140px] w-full transition-all duration-200 ${tile.tileStyles}`}
                  >
                    <div className="text-xs font-semibold tracking-wide uppercase mb-2">
                      {tile.label}
                    </div>
                    <div className="text-5xl font-bold leading-none mb-2">
                      {tile.awaitingReview}
                    </div>
                    <div className="text-xs text-slate-500">
                      {tile.total} {tile.totalLabel}
                    </div>
                  </button>
                ))}
              </div>
              <div className="text-center text-sm text-slate-500 mt-4">
                {triagedCount} email{triagedCount === 1 ? '' : 's'} triaged
                {(() => {
                  const urgentCount = getGroupCounts(['urgent']).total;
                  return urgentCount > 0 ? ` / ${urgentCount} Urgent` : '';
                })()}
              </div>
            </div>
          )}

          {/* Filter dropdown and queued button */}
          <div className={`flex flex-row flex-wrap items-center gap-3 mb-6 ${getIsMobileOnly() ? 'justify-start' : 'justify-between'}`}>
            <div className="flex flex-row items-center gap-3">
              <div className="relative" ref={filterDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsFilterDropdownOpen(prev => !prev)}
                  data-amp-track-section="Digest Email List"
                  data-amp-track-action="Filter_Dropdown_Toggle"
                  data-amp-track-description="Toggle Email Filters"
                  className={`flex flex-row items-center justify-start text-md font-semibold gap-2 px-3 py-2 border-solid border rounded-3xl border-slate-300
                    ${hasActiveFilters
                      ? 'text-white bg-slate-600 hover:bg-slate-500'
                      : 'text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white'}`}
                >
                  <Filter size={18} />
                  <span>{getActiveFilterLabel()}</span>
                  <ChevronDown size={18} className={`transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isFilterDropdownOpen && (
                  <div className="absolute left-0 z-50 mt-2 min-w-[280px] bg-white rounded-lg shadow-lg border border-slate-200 py-2">
                    <div className="flex flex-row items-center justify-between gap-3 p-2 border-b border-slate-100 mb-1">
                      <button
                        type="button"
                        onClick={handleSelectAllFilters}
                        data-amp-track-section="Digest Email List"
                        data-amp-track-action="Filter_Select_All_Click"
                        data-amp-track-description="Select All Filters"
                        className="text-sm text-sky-600 hover:bg-slate-50 ml-0 py-1"
                      >
                        Select All Filters
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveFilters([]);
                          collapseCardVisibility();
                        }}
                        className="text-sm text-sky-600 hover:bg-slate-50 ml-0 py-1"
                      >
                        Clear filters
                      </button>
                    </div>
                    {dropdownFilterTypes.map((type) => {
                      const isSelected = activeFilters.includes(type);
                      return (
                        <label
                          key={type}
                          className="flex flex-row items-center gap-3 px-4 py-2 cursor-pointer hover:bg-slate-50 text-slate-700"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleFilter(type)}
                            data-amp-track-section="Digest Email List"
                            data-amp-track-action="Filter_Checkbox_Click"
                            data-amp-track-filter_type={`${isSelected ? type + '_unselected' : type + '_selected'}`}
                            className="w-4 h-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                          />
                          <span className="flex flex-row items-center gap-2 flex-1">
                            {filterTypes[type].label}
                            <span className="text-sm font-semibold">{filterTypes[type].name}</span>
                          </span>
                          <span className="text-sm text-slate-500">{filterTypes[type].count || 0}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={handleQueuedViewToggle}
                data-amp-track-section="Digest Email List"
                data-amp-track-action="Filter_Click"
                data-amp-track-filter_type={isQueuedView ? 'unreviewed_unselected' : 'unreviewed_selected'}
                className={`flex flex-row items-center justify-start text-md font-semibold gap-2 px-3 py-2 border-solid border rounded-3xl border-slate-300
                  ${isQueuedView
                    ? 'text-white bg-slate-600 hover:bg-slate-500'
                    : 'text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white'}`}
              >
                {remainingDigestItems}
                <InboxIcon size={22} />
                <span>In queue</span>
              </button>
            </div>

            <div
              className="flex flex-row items-center overflow-hidden"
              role="group"
              aria-label="Email view mode"
            >
              <button
                type="button"
                onClick={() => setIsListView(true)}
                title="List view"
                data-amp-track-section="Digest Email List"
                data-amp-track-action="View_Toggle_Click"
                data-amp-track-description="List View"
                className={`p-2 mr-0 border-solid border rounded-l-3xl border-slate-300 border-r-0 transition-colors ${
                  isListView
                    ? 'text-white bg-slate-600 hover:bg-slate-500'
                    : 'text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white'
                }`}
                aria-pressed={isListView}
              >
                <List size={22} />
              </button>
              <button
                type="button"
                onClick={() => setIsListView(false)}
                title="Expanded view"
                data-amp-track-section="Digest Email List"
                data-amp-track-action="View_Toggle_Click"
                data-amp-track-description="Expanded View"
                className={`p-2 ml-0 border-solid border rounded-r-3xl border-slate-300 border-l-0 transition-colors ${
                  !isListView
                    ? 'text-white bg-slate-600 hover:bg-slate-500'
                    : 'text-slate-600 bg-transparent hover:bg-sky-500 hover:text-white'
                }`}
                aria-pressed={!isListView}
              >
                <Rows3 size={22} />
              </button>
            </div>
          </div>

          {remainingDigestItems > 0 && !isQueuedView && !hasActiveFilters && (itemsToRender && itemsToRender.length > 0) && searchTerm === '' && getFilteredThreads().length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-2xl font-semibold text-slate-600 mb-2">
                📥 There are emails in queue that need to be triaged.
              </div>
              <div className="text-slate-500">
                Please click <span className="font-semibold text-slate-700">Run Email Automation</span> to triage your queued emails.
              </div>
            </div>
          )}

          {(remainingDigestItems === 0 || (remainingDigestItems > 0 && (hasActiveFilters || isQueuedView))) && (itemsToRender && itemsToRender.length > 0) && searchTerm === '' && getFilteredThreads().length === 0 && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-2xl font-semibold text-slate-600 mb-2">
                🎉 No emails need review right now!
              </div>
              <div className="text-slate-500">
                {isQueuedView
                  ? 'Queued emails have been reviewed.'
                  : hasActiveFilters
                    ? `${activeFilters.map(f => filterTypes[f]?.name || f).join(', ')} emails have been reviewed.`
                    : 'All your emails have been reviewed. Check back later for new messages.'
                }
              </div>
            </div>
          )}

          {currentDigest?.content?.length === 0 ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center px-4 py-12 text-center">
              <h2 className="text-2xl font-semibold text-slate-700">No emails to review</h2>
              <p className="mt-2 text-sm text-slate-500">Your inbox is up to date. Select Sync Inbox to check again.</p>
            </div>
          ) : isListView ? (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {getFilteredThreads().map((thread, threadIndex) => {
                const threadKey = thread[0]?.threadId || thread[0]?.id || `thread-${threadIndex}`;
                const email = thread[0];

                return (
                  <div
                    key={threadKey}
                    className={`flex flex-col gap-1 py-2 px-3 border-b border-slate-200 last:border-b-0 transition-colors ${
                      email?.archived ? 'opacity-55' : ''
                    }`}
                  >
                    <div className="flex flex-row justify-between items-center gap-2">
                      <h3
                        onClick={() => setIsCardVisible(prev => ({
                          ...prev,
                          [email?.id]: !prev[email?.id]
                        }))}
                        className={`font-semibold leading-tight flex-1 min-w-0 cursor-pointer ${getIsMobileOnly() ? 'text-sm' : 'text-base'}`}
                      >
                        {renderHighlightedSubject(email?.subject)}
                      </h3>
                      {renderThreadActionButtons(email)}
                    </div>

                    <div className="flex flex-row justify-between items-center gap-2">
                      <div className="flex flex-row items-center gap-2 min-w-0 flex-1">
                        <div className={`text-stone-600 truncate flex flex-row items-center gap-1 ${getIsMobileOnly() ? 'text-xs' : 'text-sm'}`}>
                          {formatEmailSenderName(email?.from, email?.fromName)}
                          {thread.length > 1 && (
                            <>
                              <Mails size={16} className="text-stone-400 shrink-0" />
                              <span className="text-stone-400 text-xs">
                                {thread.length}
                              </span>
                            </>
                          )}
                        </div>
                        <EmailActionItems
                          item={{ ...email, actions: visibleEmailActions(email) }}
                          labels={currentDigest?.labels}
                          readOnlyMode={readOnlyMode}
                          onActionClick={(action) => handleActionClick(action, email)}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-600 shrink-0">
                        {formatEmailDate(email?.date)}
                      </span>
                    </div>

                    <div className={`transition-all duration-500 ${isCardVisible[email?.id] ? 'max-h-screen pt-1' : 'max-h-0 overflow-hidden'}`}>
                      {thread.map((threadEmail, emailIndex) => (
                        <EmailCard
                          key={threadEmail.id}
                          email={threadEmail}
                          setInputValues={setInputValues}
                          inputValues={inputValues}
                          isProjectModalOpen={isProjectModalOpen}
                          setIsProjectModalOpen={setIsProjectModalOpen}
                          onMoveEmail={handleMoveEmail}
                          onAddFolder={handleOpenCustomFolderModal}
                          onCreateTask={handleCreateTask}
                          onAddEmailToProject={handleAddEmailToProject}
                          onReply={handleReplyToEmail}
                          onViewTask={handleViewTask}
                          onViewProject={handleViewProject}
                          aiActionData={threadEmail.aiActionData}
                          emailIndex={emailIndex}
                          currentDigest={currentDigest}
                          todaysDigest={currentDigest}
                          getActiveTask={getActiveTask}
                          getActiveProject={getActiveProject}
                          threadLength={thread.length}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            getFilteredThreads().map((thread, threadIndex) => {
              const threadKey = thread[0]?.threadId || thread[0]?.id || `thread-${threadIndex}`;
              const email = thread[0];

              return (
                <div key={threadKey} className="flex flex-row justify-start gap-1 mb-3">
                  <div
                    key={`${threadKey}-inner`}
                    className={`relative w-full mb-2 bg-white border border-slate-200 rounded-lg p-2 ${
                      email?.archived ? 'opacity-55' : 'shadow-sm shadow-slate-200'
                    }`}
                  >
                    <div className="flex flex-row justify-between items-start mb-1">
                      <div
                        onClick={() => setIsCardVisible(prev => ({
                          ...prev,
                          [email?.id]: !prev[email?.id]
                        }))}
                        className="w-3/4 pr-2 pl-1 cursor-pointer flex flex-col justify-between gap-1"
                      >
                        <h3 className="text-xl font-semibold leading-tight">
                          {renderHighlightedSubject(email?.subject)}
                        </h3>
                      </div>

                      <div className="flex w-1/4 justify-end items-start">
                        {renderThreadActionButtons(email)}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between items-center gap-2 pl-1 mb-2">
                      <div className="flex flex-row justify-start items-center gap-2">
                        {thread.length > 1 && (
                          <p className="text-sm text-stone-400 flex items-center gap-1">
                            <Mails size={18} />
                          </p>
                        )}
                        <p className={`text-stone-600 ${getIsMobileOnly() ? 'text-xs' : 'text-sm'}`}>
                          {email?.from}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm pl-2 font-semibold text-gray-600">
                          {formatEmailDate(email?.date)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <EmailActionRibbon
                        item={{ ...email, actions: visibleEmailActions(email) }}
                        labels={currentDigest?.labels}
                        readOnlyMode={readOnlyMode}
                        onActionClick={(action) => handleActionClick(action, email)}
                      />
                    </div>

                    <div className={`transition-all duration-500 ${isCardVisible[email?.id] ? 'max-h-screen pt-1' : 'max-h-0 overflow-hidden'}`}>
                      {thread.map((threadEmail, emailIndex) => (
                        <EmailCard
                          key={threadEmail.id}
                          email={threadEmail}
                          setInputValues={setInputValues}
                          inputValues={inputValues}
                          isProjectModalOpen={isProjectModalOpen}
                          setIsProjectModalOpen={setIsProjectModalOpen}
                          onMoveEmail={handleMoveEmail}
                          onAddFolder={handleOpenCustomFolderModal}
                          onCreateTask={handleCreateTask}
                          onAddEmailToProject={handleAddEmailToProject}
                          onReply={handleReplyToEmail}
                          onViewTask={handleViewTask}
                          onViewProject={handleViewProject}
                          aiActionData={threadEmail.aiActionData}
                          emailIndex={emailIndex}
                          currentDigest={currentDigest}
                          todaysDigest={currentDigest}
                          getActiveTask={getActiveTask}
                          getActiveProject={getActiveProject}
                          threadLength={thread.length}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      <CustomFolderModal
        isOpen={isCustomFolderModalOpen}
        onClose={handleCloseCustomFolderModal}
        mailIntegrationId={customFolderModalMailboxIdRef.current}
        getToken={getToken}
        onSaved={handleCustomFolderSaved}
      />

      <EmailPrefsModal
        isOpen={isEmailPrefsModalOpen && emailPrefsCurrentView === "main" && !isCustomFolderModalOpen}
        onClose={handleClose}
        onNavigateToInstructions={handleNavigateToInstructions}
        onNavigateToSchedule={handleNavigateToSchedule}
        onNavigateToFolders={handleNavigateToFolders}
        readOnlyMode={readOnlyMode}
        setReadOnlyMode={setReadOnlyMode}
        showEmailTriageOnboardingModal={showEmailTriageOnboardingModal}
        provider={mailbox?.provider}
      />

      <EmailCustomFolders
        isOpen={isEmailPrefsModalOpen && emailPrefsCurrentView === "folders" && !isCustomFolderModalOpen}
        onClose={handleClose}
        onBack={handleBackToMain}
        mailIntegrationId={mailbox?.id}
        getToken={getToken}
        onOpenCustomFolderModal={handleOpenCustomFolderModal}
        onDestinationsChanged={handleCustomFolderSaved}
      />

      <EmailProcessingInstructions
        isOpen={isEmailPrefsModalOpen && emailPrefsCurrentView === "instructions"}
        onClose={handleClose}
        onBack={handleBackToMain}
        onSave={handleSaveInstructions}
      />

      <EmailProcessingSchedule
        isOpen={isEmailPrefsModalOpen && emailPrefsCurrentView === "schedule"}
        onClose={handleClose}
        onBack={handleBackToMain}
        onSave={handleSaveSchedule}
      />

      <ActionDetailsModal
        isOpen={isActionModalOpen}
        onClose={handleCloseActionModal}
        action={selectedAction}
        email={selectedEmail && { ...selectedEmail, actions: visibleEmailActions(selectedEmail) }}
        labels={currentDigest?.labels}
        readOnlyMode={readOnlyMode}
        onRevert={selectedAction && (
          !['archive', 'move-to-label'].includes(selectedAction.type)
          || canWriteEmail(selectedEmail, 'restore_email')
        )
          ? () => handleUndoAutomatedActionWithUpdate(selectedEmail.id, selectedAction.type)
          : undefined}
        onAcknowledge={handleAcknowledgeAction}
        onArchive={canWriteEmail(selectedEmail, 'archive_email') ? handleArchiveEmail : undefined}
        mailWritesDisabled={!canWriteEmail(selectedEmail, 'archive_email')}
        getEmailContent={getEmailContent}
        provider={mailbox?.provider}
      />
    </div>
  );
};

export default FilteredEmailView;
